import { ISalePageDetailV2Response } from '@/api/types';
import { Tag } from '@/components';
import { IMoneyResponse } from '@/types/common';
import { Column, Element, Icon, Row, Skeleton } from '@vuddy/components';
import { SalePageInstructions } from '../instructions';
import * as styles from './classSummaryContents.css';
import SummaryCTA from './SummaryCTA';

type ClassPricingMeta = {
  highlightLabel?: string;
  priceDescriptionLabel?: string;
  discountBadgeLabel?: string;
  normalPriceLabel?: string;
  secondaryPrice?: string;
  noticeLabel: string;
  benefitLabel?: string;
  installmentMonth?: number;
};

const formatMoney = (money?: IMoneyResponse) =>
  typeof money?.amount === 'number'
    ? `${money.amount.toLocaleString()}원`
    : undefined;

const hasFreeClassVoucher = (salePage?: Partial<ISalePageDetailV2Response>) => {
  return (
    salePage?.clazz?.classProducts?.some(product => {
      const price = product.discountPrice ?? product.normalPrice;
      return price?.amount === 0;
    }) ?? false
  );
};

const getClassPricingMeta = (
  salePage?: Partial<ISalePageDetailV2Response>
): ClassPricingMeta | null => {
  if (!salePage || salePage.type !== 'CLASS') return null;
  const detail = salePage.clazz?.detail;
  if (!detail) return null;

  const priceDescriptionLabel =
    detail.installmentPrice ?? salePage.priceForDisplay ?? '';

  return {
    highlightLabel: detail.subtitle,
    priceDescriptionLabel,
    discountBadgeLabel: detail.discountLabel ?? undefined,
    normalPriceLabel: detail.normalPrice?.amount
      ? `정상가 ${formatMoney(detail.normalPrice)}`
      : undefined,
    noticeLabel: '결제 완료 후 강의실에서 다시보기 제공',
    benefitLabel: hasFreeClassVoucher(salePage)
      ? '0원 체험 이용권 제공'
      : undefined,
    installmentMonth: detail.installmentMonth,
  };
};

const ClassSummary = ({
  salePage,
  hidePurchaseCTAButton,
  isShowSummaryCTA,
  salePageId,
}: {
  salePage: Partial<ISalePageDetailV2Response>;
  hidePurchaseCTAButton: boolean;
  isShowSummaryCTA: boolean;
  salePageId?: number;
}) => {
  const classPricingMeta = getClassPricingMeta(salePage);

  return (
    <Column className={styles.summaryContentWrapper}>
      <Column gap="gap_large">
        {/* 타이틀 영역 */}
        <Column gap="gap_small">
          <Element className={styles.title} typography="titleSmall">
            {salePage?.name ?? <Skeleton />}
          </Element>

          {classPricingMeta && (
            <Element
              typography="bodyXs"
              color="secondary"
              className={styles.classPriceDescriptor}>
              {classPricingMeta.highlightLabel}
            </Element>
          )}
        </Column>

        {/* 가격 영역 - 클래스를 제외한 나머지 상품 */}
        {salePage?.priceForDisplay && (
          <Column gap="gap_2xs">
            <Element typography="titleBase" color="vuddyPrimary">
              {salePage?.priceForDisplay ?? <Skeleton />}
            </Element>
          </Column>
        )}
      </Column>

      {/* 클래스 가격 영역 */}
      {classPricingMeta && (
        <>
          {classPricingMeta.priceDescriptionLabel && (
            <Column gap="gap_small">
              <Element>
                {classPricingMeta.normalPriceLabel && (
                  <Element
                    typography="captionBase"
                    color="tertiary"
                    className={styles.classPriceMeta}>
                    {classPricingMeta.normalPriceLabel}
                  </Element>
                )}
              </Element>
              <Row gap="gap_base" alignItems="center" flexWrap="wrap">
                {classPricingMeta.discountBadgeLabel && (
                  <Tag
                    size="medium"
                    variant="red"
                    label={classPricingMeta.discountBadgeLabel}
                  />
                )}
                <Element
                  color="primary"
                  className={styles.classPriceDescription}>
                  {classPricingMeta.priceDescriptionLabel}
                </Element>
                {classPricingMeta.normalPriceLabel && (
                  <Element typography="captionBase" color="tertiary">
                    {classPricingMeta.installmentMonth
                      ? `${classPricingMeta.installmentMonth}개월 할부가`
                      : '할부가'}
                  </Element>
                )}
              </Row>
            </Column>
          )}
          {classPricingMeta.secondaryPrice && (
            <Element typography="bodySmall" color="secondary">
              {classPricingMeta.secondaryPrice}
            </Element>
          )}
        </>
      )}

      {/* 펀딩 가격 영역 */}
      {hidePurchaseCTAButton && (
        <Element
          // marginTop="gap_xl"
          backgroundColor="tertiary"
          borderRadius="radius_base"
          paddingY="padding_2xs"
          paddingX="padding_xs">
          <Row gap="gap_xs" alignItems="center">
            <Icon name="FILL_INFORMATION_CIRCLE" size="16px" color="tertiary" />
            <Element typography="captionBaseAccent" color="tertiary">
              웹에서만 구매 가능합니다.
            </Element>
          </Row>
        </Element>
      )}
      <SalePageInstructions salePage={salePage} />
      {isShowSummaryCTA && salePageId && <SummaryCTA salePageId={salePageId} />}
    </Column>
  );
};

export default ClassSummary;
